import { useQuery } from '@tanstack/react-query';
import { Info, MapPin, Megaphone, Plane, X } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api.ts';
import { useOfficeStatus } from '../lib/office.ts';
import type { Announcement } from '../lib/types.ts';

export const announcementsKey = ['announcements'] as const;

/** Active announcements. Reloaded live (see live.ts), on focus and every 5 minutes. */
export function useAnnouncements() {
  return useQuery({
    queryKey: announcementsKey,
    queryFn: () => api.get<{ items: Announcement[] }>('/api/announcements'),
    refetchInterval: 5 * 60_000,
    select: (d) => d.items,
  });
}

const DISMISSED_KEY = 'helpdesk.dismissed-announcements';

function readDismissed(): number[] {
  try {
    return JSON.parse(localStorage.getItem(DISMISSED_KEY) ?? '[]') as number[];
  } catch {
    return [];
  }
}

const timeOnly = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });
const dayAndTime = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  hour: 'numeric',
  minute: '2-digit',
});

/** "until 3:00 PM", or with the day when it isn't today. */
export function untilText(endsAt: string | null): string {
  if (!endsAt) return '';
  const end = new Date(endsAt);
  const sameDay = end.toDateString() === new Date().toDateString();
  return `until ${(sameDay ? timeOnly : dayAndTime).format(end)}`;
}

export function AnnouncementText({ a }: { a: Announcement }) {
  return (
    <>
      {a.office && <strong>{a.office}: </strong>}
      {a.message}
      {a.endsAt && <span className="opacity-75"> ({untilText(a.endsAt)})</span>}
    </>
  );
}

export const toneClasses = (tone: Announcement['tone']) =>
  tone === 'outage' ? 'border-warning/40 bg-warning-soft text-text' : 'border-info/30 bg-info-soft text-text';

const time = (iso: string) =>
  new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
const longDay = (ymd: string) =>
  new Date(`${ymd}T12:00:00Z`).toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

/**
 * Where IT is: on leave, off today (the reason stays private), or out on
 * official duty. Shown in the banner and on the report form.
 */
export function CoverNotices() {
  const { data } = useOfficeStatus();
  if (!data?.cover.length && !data?.out.length) return null;
  const today = new Date().toISOString().slice(0, 10);
  const box = `flex items-start gap-3 rounded-lg border px-4 py-3 text-sm ${toneClasses('notice')}`;
  return (
    <>
      {data.cover.map((c) => (
        <div key={c.away} className={box}>
          <Plane className="mt-0.5 size-4 shrink-0" aria-hidden />
          <p>
            <strong>{c.away}</strong>{' '}
            {c.reason === 'off' && c.until === today
              ? 'is out of the office today.'
              : `is ${c.reason === 'off' ? 'out of the office' : 'on leave'} until ${longDay(c.until)}.`}{' '}
            {c.cover ? (
              <>
                <strong>{c.cover}</strong> is covering, so report problems as usual.
              </>
            ) : (
              'Report problems as usual; they’ll be picked up remotely where possible.'
            )}
          </p>
        </div>
      ))}
      {data.out.map((o) => (
        <div key={o.name} className={box}>
          <MapPin className="mt-0.5 size-4 shrink-0" aria-hidden />
          <p>
            <strong>{o.name}</strong> is out of the office on official duty until {time(o.until)}. Your
            reports still reach IT; if a phone call would do, choose “Help me remotely”.
          </p>
        </div>
      ))}
    </>
  );
}

/** Under the header on every page. Each person can hide an announcement they've read. */
export function AnnouncementBanner() {
  const { data } = useAnnouncements();
  const [dismissed, setDismissed] = useState(readDismissed);
  const shown = (data ?? []).filter((a) => !dismissed.includes(a.id));
  const office = useOfficeStatus();
  if (shown.length === 0 && !office.data?.cover.length && !office.data?.out.length) return null;

  const dismiss = (id: number) => {
    // Keep only ids that are still active, so the list never grows.
    const next = [...dismissed.filter((d) => data?.some((a) => a.id === d)), id];
    setDismissed(next);
    try {
      localStorage.setItem(DISMISSED_KEY, JSON.stringify(next));
    } catch {
      // Storage unavailable: hidden until the page reloads.
    }
  };

  return (
    <div role="region" aria-label="Announcements from IT" className="space-y-2">
      <CoverNotices />
      {shown.map((a) => {
        const Icon = a.tone === 'outage' ? Megaphone : Info;
        return (
          <div
            key={a.id}
            className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-sm ${toneClasses(a.tone)}`}
          >
            <Icon className="mt-0.5 size-4 shrink-0" aria-hidden />
            <p className="min-w-0 flex-1">
              <AnnouncementText a={a} />
            </p>
            <button
              type="button"
              onClick={() => dismiss(a.id)}
              aria-label="Hide this announcement"
              className="-m-1 rounded p-1 opacity-70 hover:opacity-100"
            >
              <X className="size-4" aria-hidden />
            </button>
          </div>
        );
      })}
    </div>
  );
}

/**
 * On the report form: what IT already knows about, so people can skip
 * reporting it. Not dismissible, since this is where it matters.
 */
export function KnownIssues() {
  const { data } = useAnnouncements();
  const outages = (data ?? []).filter((a) => a.tone === 'outage');
  if (outages.length === 0) return null;
  return (
    <div className={`rounded-lg border p-4 text-sm ${toneClasses('outage')}`}>
      <p className="flex items-center gap-2 font-semibold">
        <Megaphone className="size-4" aria-hidden />
        IT already knows about {outages.length === 1 ? 'this' : 'these'}
      </p>
      <ul className="mt-1.5 list-disc space-y-1 pl-6">
        {outages.map((a) => (
          <li key={a.id}>
            <AnnouncementText a={a} />
          </li>
        ))}
      </ul>
      <p className="mt-2 text-muted">If your problem is one of these, there’s no need to report it.</p>
    </div>
  );
}
